import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { api } from '@/runtime'
import type {
  LibraryAdd,
  LibraryHit,
  LibraryItem,
  LibraryKind,
  LibraryPatch,
  LibrarySources,
  LibraryStatus,
} from '@/db/types'
import { Button, Empty, Failed, Field, SectionTitle, Segmented, Sheet, cx } from '@/components/ui'
import { LibraryHeader } from './Music'

/**
 * The library: things from outside that matter, starting as a wish list for
 * films, series and games. It is online only by design: rows live in their own
 * tables beside the sync model, so nothing here goes through Dexie or the
 * engine (docs/decisions.md, "The library lives outside the sync model"). The
 * view fetches on open, writes straight to the API, and says so when offline.
 *
 * Titles are looked up in the catalogues as you type (TMDB for films and
 * series, the Steam store for games) so a row carries artwork, a link and a
 * blurb; a title with no match is still added as it is.
 */
const KINDS: { value: LibraryKind; label: string; plural: string; icon: string }[] = [
  { value: 'film', label: 'Film', plural: 'Films', icon: '🎬' },
  { value: 'tv', label: 'TV', plural: 'TV', icon: '📺' },
  { value: 'game', label: 'Game', plural: 'Games', icon: '🎮' },
]
const STATUSES: { value: LibraryStatus; label: string; verb: Record<LibraryKind, string> }[] = [
  {
    value: 'want',
    label: 'Want',
    verb: { film: 'Want to watch', tv: 'Want to watch', game: 'Want to play' },
  },
  { value: 'doing', label: 'Doing', verb: { film: 'Watching', tv: 'Watching', game: 'Playing' } },
  { value: 'done', label: 'Done', verb: { film: 'Watched', tv: 'Watched', game: 'Played' } },
  { value: 'dropped', label: 'Dropped', verb: { film: 'Dropped', tv: 'Dropped', game: 'Dropped' } },
]
const kindOf = (k: string) => KINDS.find((x) => x.value === k) ?? KINDS[0]!
const SOURCE_NAME: Record<string, string> = { tmdb: 'TMDB', steam: 'Steam' }
const NO_SOURCES: LibrarySources = { tmdb: false, steam: false }

const errorText = (e: unknown) =>
  !navigator.onLine
    ? 'You are offline. The library needs a connection.'
    : e instanceof Error
      ? e.message
      : 'Failed'

const fmtPlaytime = (mins: number) => (mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`)

/** Poster or header art, or the kind's icon when there is none (or it fails to load). */
const Art = ({ item, size }: { item: { kind: string; data: { image?: string } }; size: 'row' | 'hero' }) => {
  const k = kindOf(item.kind)
  const [failed, setFailed] = useState<string | null>(null)
  if (!item.data.image || failed === item.data.image) {
    return (
      <span
        className={cx(
          'shrink-0 flex items-center justify-center',
          size === 'row'
            ? 'w-10 h-14 text-lg'
            : 'w-full h-40 text-4xl rounded-[var(--radius-sheet-inner)] bg-raised',
        )}
        aria-hidden
      >
        {k.icon}
      </span>
    )
  }
  return (
    <img
      src={item.data.image}
      alt=""
      loading="lazy"
      onError={() => setFailed(item.data.image ?? null)}
      className={cx(
        'shrink-0 object-cover bg-raised',
        size === 'row' ? 'w-10 h-14 rounded-[6px]' : 'w-full max-h-56 rounded-[var(--radius-sheet-inner)]',
        // steam header art is wide; posters are tall
        size === 'row' && item.kind === 'game' && 'w-[60px] h-[28px]',
      )}
    />
  )
}

export const LibraryView = () => {
  const [items, setItems] = useState<LibraryItem[] | null>(null)
  const [sources, setSources] = useState<LibrarySources>(NO_SOURCES)
  const [error, setError] = useState<string | null>(null)
  const [kind, setKind] = useState<LibraryKind | 'all'>('all')
  const [status, setStatus] = useState<LibraryStatus>('want')
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [syncMsg, setSyncMsg] = useState<string | null>(null)

  // promise callbacks, not a synchronous setState in the effect: the compiler rule
  const load = useCallback(
    () =>
      api
        // the wish list's own kinds: the music connectors' tracks never reach it
        .libraryList({ kind: KINDS.map((k) => k.value).join(',') })
        .then((r) => {
          setItems(r.items)
          setSources(r.sources ?? NO_SOURCES)
          setError(null)
        })
        .catch((e: unknown) => setError(errorText(e))),
    [],
  )
  useEffect(() => {
    load()
  }, [load])

  const syncSteam = async () => {
    setSyncMsg('Syncing…')
    try {
      const r = await api.librarySyncSteam()
      setSyncMsg(`${r.games} games, ${r.added} new`)
      await load()
    } catch (e) {
      setSyncMsg(errorText(e))
    }
  }

  const shown = (items ?? []).filter((i) => i.status === status && (kind === 'all' || i.kind === kind))
  const open = items?.find((i) => i.id === openId) ?? null
  const upsert = (it: LibraryItem) =>
    setItems((cur) => {
      const rest = (cur ?? []).filter((x) => x.id !== it.id)
      return [it, ...rest]
    })

  return (
    <div data-testid="library">
      <LibraryHeader
        music={false}
        right={
          <Button variant="primary" onClick={() => setAdding(true)} data-testid="library-add">
            Add
          </Button>
        }
      />
      {
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Segmented
              label="Status"
              value={status}
              onChange={setStatus}
              options={STATUSES.map((s) => ({ value: s.value, label: s.label }))}
            />
            <Segmented
              label="Kind"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'all' as const, label: 'All' },
                ...KINDS.map((k) => ({ value: k.value, label: k.plural })),
              ]}
            />
          </div>
          {error && (
            <Failed
              what="your films, TV and games"
              reason={error}
              onRetry={() => void load()}
              className="mb-3"
            />
          )}
          {items && shown.length === 0 && !error && (
            <Empty icon={kind === 'all' ? '🍿' : kindOf(kind).icon}>
              {status === 'want'
                ? 'Nothing on the list. When someone recommends something, put it here.'
                : 'Nothing here.'}
            </Empty>
          )}
          {shown.length > 0 && (
            <ul className="group-list" data-testid="library-list">
              {shown.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(it.id)}
                    className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-raised/60"
                    data-library-id={it.id}
                  >
                    <Art item={it} size="row" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] truncate">
                        {it.title}
                        {it.year && <span className="text-faint ml-1.5 text-sm">{it.year}</span>}
                      </span>
                      {(it.data.recommended_by || it.notes || it.data.playtime_minutes) && (
                        <span className="flex gap-x-2.5 text-xs text-faint truncate">
                          {it.data.recommended_by && (
                            <span className="shrink-0">From {it.data.recommended_by}</span>
                          )}
                          {it.notes && <span className="truncate">{it.notes}</span>}
                          {!it.data.recommended_by && !it.notes && it.data.playtime_minutes
                            ? `${fmtPlaytime(it.data.playtime_minutes)} played`
                            : ''}
                        </span>
                      )}
                    </span>
                    {it.rating && (
                      <span className="text-xs text-muted shrink-0" aria-label={`Rated ${it.rating} of 5`}>
                        {'★'.repeat(it.rating)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {sources.steam && (
            <p className="text-xs text-faint mt-6 flex items-center gap-2">
              <Button variant="chip" onClick={() => void syncSteam()} data-testid="library-sync-steam">
                Sync Steam now
              </Button>
              <span>{syncMsg ?? 'Owned games arrive by themselves every six hours.'}</span>
            </p>
          )}
        </div>
      }
      <AddSheet
        open={adding}
        onClose={() => setAdding(false)}
        defaultKind={kind === 'all' ? 'film' : kind}
        sources={sources}
        onAdded={(it) => {
          upsert(it)
          setStatus(it.status)
          setAdding(false)
        }}
      />
      <ItemSheet
        item={open}
        onClose={() => setOpenId(null)}
        onChanged={upsert}
        onRemoved={(id) => {
          setItems((cur) => (cur ?? []).filter((x) => x.id !== id))
          setOpenId(null)
        }}
      />
    </div>
  )
}

const inputCls = 'w-full'

const AddSheet = ({
  open,
  onClose,
  defaultKind,
  sources,
  onAdded,
}: {
  open: boolean
  onClose: () => void
  defaultKind: LibraryKind
  sources: LibrarySources
  onAdded: (it: LibraryItem) => void
}) => {
  const [kind, setKind] = useState<LibraryKind>(defaultKind)
  const [title, setTitle] = useState('')
  const [year, setYear] = useState('')
  const [from, setFrom] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // the catalogue lookup: hits for what is typed, and the one that was picked
  const [hits, setHits] = useState<LibraryHit[]>([])
  const [pick, setPick] = useState<LibraryHit | null>(null)
  const searchSeq = useRef(0)
  const canSearch = kind === 'game' || sources.tmdb

  useEffect(() => {
    if (!open || pick || !canSearch || title.trim().length < 2) {
      searchSeq.current++
      return
    }
    const seq = ++searchSeq.current
    const q = title.trim()
    const t = setTimeout(() => {
      api
        .librarySearch(kind, q)
        .then((r) => {
          if (seq === searchSeq.current) setHits(r.hits)
        })
        .catch(() => {
          if (seq === searchSeq.current) setHits([])
        })
    }, 300)
    return () => clearTimeout(t)
  }, [open, pick, canSearch, title, kind])

  const reset = () => {
    setTitle('')
    setYear('')
    setFrom('')
    setNotes('')
    setHits([])
    setPick(null)
    setError(null)
  }
  const choose = (h: LibraryHit) => {
    setPick(h)
    setHits([])
    setTitle(h.title)
    if (h.year) setYear(String(h.year))
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim() || busy) return
    setBusy(true)
    try {
      const body: LibraryAdd = { kind, title: title.trim() }
      if (year.trim()) body.year = Number(year)
      if (from.trim()) body.recommended_by = from.trim()
      if (notes.trim()) body.notes = notes.trim()
      if (pick) body.pick = pick
      const r = await api.libraryAdd(body)
      onAdded(r.item)
      reset()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add to the library"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            form="library-add-form"
            type="submit"
            disabled={!title.trim() || busy}
            data-testid="library-save"
          >
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </div>
      }
    >
      <form id="library-add-form" onSubmit={(e) => void submit(e)}>
        <div className="mb-3">
          <Segmented
            label="Kind"
            value={kind}
            onChange={(k) => {
              setKind(k)
              setPick(null)
              setHits([])
            }}
            options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
          />
        </div>
        <Field
          label="Title"
          hint={canSearch && !pick ? 'Matches from the catalogue appear as you type.' : undefined}
        >
          <input
            className={inputCls}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value)
              setPick(null)
            }}
            autoFocus
            autoComplete="off"
            placeholder={kind === 'game' ? 'Outer Wilds' : 'Heat'}
            data-testid="library-title"
          />
        </Field>
        {hits.length > 0 && (
          <ul
            className="-mt-1 mb-3 card overflow-hidden"
            data-testid="library-hits"
            role="listbox"
            aria-label="Matches"
          >
            {hits.map((h) => (
              <li key={`${h.source}:${h.external_id}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => choose(h)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-raised"
                >
                  <Art item={{ kind: h.kind, data: { image: h.image } }} size="row" />
                  <span className="min-w-0">
                    <span className="block text-sm truncate">
                      {h.title}
                      {h.year && <span className="text-faint ml-1.5">{h.year}</span>}
                    </span>
                    {h.overview && (
                      <span className="block text-xs text-faint line-clamp-1">{h.overview}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {pick && (
          <p className="-mt-1 mb-3 text-xs text-faint flex items-center gap-2" data-testid="library-picked">
            <span>
              Matched on {SOURCE_NAME[pick.source] ?? pick.source}
              {pick.year ? ` (${pick.year})` : ''}.
            </span>
            <button type="button" className="underline" onClick={() => setPick(null)}>
              Not this one
            </button>
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Year">
            <input
              className={inputCls}
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="optional"
            />
          </Field>
          <Field label="Recommended by">
            <input
              className={inputCls}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="optional"
              data-testid="library-from"
            />
          </Field>
        </div>
        <Field label="Notes">
          <textarea
            className={cx(inputCls, 'min-h-20')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why, or what they said about it"
            data-testid="library-notes"
          />
        </Field>
        {error && (
          <p className="text-sm text-fg-2" role="alert">
            {error}
          </p>
        )}
      </form>
    </Sheet>
  )
}

const ItemSheet = ({
  item,
  onClose,
  onChanged,
  onRemoved,
}: {
  item: LibraryItem | null
  onClose: () => void
  onChanged: (it: LibraryItem) => void
  onRemoved: (id: string) => void
}) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // notes are edited in place and saved on blur; everything else is one tap
  const [notes, setNotes] = useState('')
  const [notesFor, setNotesFor] = useState<string | null>(null)
  if (item && notesFor !== item.id) {
    setNotes(item.notes)
    setNotesFor(item.id)
  }
  const patch = async (p: LibraryPatch) => {
    if (!item || busy) return
    setBusy(true)
    try {
      const r = await api.libraryPatch(item.id, p)
      onChanged(r.item)
      setError(null)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  const remove = async () => {
    if (!item || busy) return
    setBusy(true)
    try {
      await api.libraryRemove(item.id)
      onRemoved(item.id)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  if (!item) return null
  const k = kindOf(item.kind)
  const meta = [
    k.label,
    item.year,
    item.data.recommended_by ? `from ${item.data.recommended_by}` : '',
    item.data.playtime_minutes ? `${fmtPlaytime(item.data.playtime_minutes)} played` : '',
  ].filter(Boolean)
  return (
    <Sheet
      open
      onClose={onClose}
      title={item.title}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="danger" onClick={() => void remove()} disabled={busy} data-testid="library-remove">
            Remove
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      }
    >
      {item.data.image && (
        <div className="mb-3">
          <Art item={item} size="hero" />
        </div>
      )}
      <p className="text-xs text-faint mb-1 flex flex-wrap gap-x-2.5">
        {meta.map((m) => (
          <span key={m}>{m}</span>
        ))}
      </p>
      {item.data.url && (
        <p className="text-xs mb-3">
          <a
            href={item.data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-muted"
            data-testid="library-link"
          >
            Open on {SOURCE_NAME[item.source] ?? item.source} ↗
          </a>
        </p>
      )}
      {item.data.overview && <p className="text-sm text-fg-2 mb-4 line-clamp-4">{item.data.overview}</p>}
      <SectionTitle>Status</SectionTitle>
      <div className="flex flex-wrap gap-1.5 mb-4" role="radiogroup" aria-label="Status">
        {STATUSES.map((s) => (
          <Button
            key={s.value}
            variant={item.status === s.value ? 'chipOn' : 'chip'}
            role="radio"
            aria-checked={item.status === s.value}
            onClick={() => void patch({ status: s.value })}
            data-testid={`library-status-${s.value}`}
          >
            {s.verb[k.value]}
          </Button>
        ))}
      </div>
      <SectionTitle>Rating</SectionTitle>
      <div className="flex gap-1 mb-4" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={item.rating === n}
            aria-label={`${n} of 5`}
            onClick={() => void patch({ rating: item.rating === n ? null : n })}
            className={cx(
              'text-xl leading-none px-0.5',
              item.rating && n <= item.rating ? 'text-accent' : 'text-faint',
            )}
          >
            ★
          </button>
        ))}
      </div>
      <Field label="Notes">
        <textarea
          className="w-full min-h-24"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => {
            if (notes !== item.notes) void patch({ notes })
          }}
          data-testid="library-item-notes"
        />
      </Field>
      {error && (
        <p className="text-sm text-fg-2" role="alert">
          {error}
        </p>
      )}
    </Sheet>
  )
}
